/**
 * bandGeometry - Zaun/Mauer/Rasenkante am Hang stufen (#684, Teil B)
 *
 * Reine, WebGL-freie Logik (keine React-/three-/fabric-Imports) — wie der
 * restliche scene3d-Adapter. Rückgabe sind fertige positions/indices, die
 * ExtrusionMesh nur noch in eine BufferGeometry hängt.
 *
 * Warum nicht drapieren wie eine Fläche (Teil A): Ein Zaun, dessen Oberkante
 * dem Boden nachzeichnet, sieht genauso falsch aus wie der waagerechte Riegel
 * von vorher. Real wird am Hang gestuft gebaut — pro Abschnitt ein eigener
 * Fuß, die Oberkante darüber waagerecht.
 *
 * Pro Schritt:
 *   baseY = min(Gelände unter dem Schritt)          ⇒ schwebt nie
 *   topY  = max(Gelände unter dem Schritt) + Höhe   ⇒ versinkt nie
 *
 * Der Höhenüberschuss am unteren Ende ist damit Steigung × Schrittlänge — bei
 * einer Stützmauer ist genau das richtig, beim Zaun bleibt es unauffällig.
 * Deshalb EINE Formel für beide statt eines Zaun-Sonderwegs.
 *
 * #698 ergänzt Öffnungen (verlinkte Tore). Sie werden als Bogenlängen-Intervall
 * auf der Polylinie ausgedrückt, nicht als Geometrie-Operation — damit greift
 * dieselbe Zerlegung auch im flachen Garten, der gar keine Abschnitte hat
 * (siehe planBandPieces).
 */

/** Ausgangs-/Höchstlänge (Meter) eines Bandabschnitts. */
export const BAND_STEP_M = 0.5;
/** Kürzester Abschnitt — die Terrain-Rasterweite; feiner löst das Gelände nicht auf. */
export const BAND_STEP_MIN_M = 0.125;
/** Höchster Geländeversatz innerhalb eines Abschnitts (Meter). */
export const BAND_MAX_RISE_M = 0.08;

/**
 * Mindesthöhe eines Sturzes über einer Öffnung (#698).
 *
 * Bleibt über einem Tor weniger übrig, entfällt der Rest ganz: ein 2-cm-Riegel
 * über dem Tor ist optisch ein Artefakt, kein Sturz. Fängt zugleich den Fall
 * ab, dass Tor und Zaun exakt gleich hoch eingestellt sind (#684-Sichttest).
 */
export const MIN_LINTEL_M = 0.05;

const EPS = 1e-9;

/**
 * Zulässiger Geländeversatz je Abschnitt für ein Band dieser Höhe.
 *
 * Der Versatz ist genau das, was am oberen Ende eines Abschnitts im Boden
 * verschwindet und am unteren zu viel aufragt — eine feste Schrittlänge taugt
 * dafür nicht, weil er zusätzlich von der Steigung abhängt: 0,5-m-Schritte sind
 * an einem 10-%-Hang 5 cm und an einer 55-%-Böschung 28 cm. Gedeckelt wird
 * deshalb der Versatz selbst, die Schrittlänge ergibt sich daraus (siehe
 * refineStepsByRise).
 *
 * Die Kopplung an die Bandhöhe fängt niedrige Bänder ab: bei einer 5-cm-
 * Rasenkante wären 8 cm Versatz höher als das Band selbst.
 */
export function maxRiseFor(heightM) {
  return Math.min(BAND_MAX_RISE_M, Math.max(0.01, heightM * 0.5));
}

/**
 * Polylinie in Schritte von höchstens stepM zerlegen.
 *
 * Geteilt wird nur INNERHALB eines Segments — eine gezeichnete Ecke bleibt
 * damit exakt eine Schrittgrenze und wird nie über zwei Richtungen gemittelt.
 *
 * @returns {Array<{a, b, atStart: boolean, atEnd: boolean}>} atStart/atEnd
 *   markieren Schritte an einer INNEREN Polylinien-Ecke (dort wird das
 *   Rechteck verlängert, um die Außenecke zu füllen).
 */
export function splitPolylineIntoSteps(polyline, stepM) {
  const steps = [];
  const segments = polyline.length - 1;
  for (let i = 0; i < segments; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (!(len > EPS)) continue;
    const n = Math.max(1, Math.ceil(len / stepM));
    for (let k = 0; k < n; k++) {
      const t0 = k / n;
      const t1 = (k + 1) / n;
      steps.push({
        a: { x: a.x + dx * t0, z: a.z + dz * t0 },
        b: { x: a.x + dx * t1, z: a.z + dz * t1 },
        // Nur an inneren Ecken verlängern: an den beiden Bandenden würde das
        // Band sonst um die Bandbreite länger als gezeichnet.
        atStart: k === 0 && i > 0,
        atEnd: k === n - 1 && i < segments - 1,
      });
    }
  }
  return steps;
}

/** Höhen an Anfang, Mitte und Ende eines Abschnitts. */
function sampleStep(step, elevationAt) {
  const mid = { x: (step.a.x + step.b.x) / 2, z: (step.a.z + step.b.z) / 2 };
  const h0 = elevationAt(step.a);
  const h1 = elevationAt(mid);
  const h2 = elevationAt(step.b);
  return {
    mid,
    min: Math.min(h0, h1, h2),
    max: Math.max(h0, h1, h2),
  };
}

/**
 * Abschnitte halbieren, bis ihr Geländeversatz unter maxRise liegt.
 *
 * Steile Böschungen bekommen so von allein kurze Abschnitte (das Band schmiegt
 * sich an), flache Strecken bleiben bei der Regellänge (wenige Quader, sichtbare
 * Mauerstufen). Untergrenze ist die Terrain-Rasterweite — feiner ist das
 * Gelände selbst nicht bekannt.
 */
export function refineStepsByRise(steps, elevationAt, maxRise, minLenM = BAND_STEP_MIN_M) {
  const out = [];
  const refine = (step) => {
    const len = Math.hypot(step.b.x - step.a.x, step.b.z - step.a.z);
    const sample = sampleStep(step, elevationAt);
    // Geteilt wird nur, solange BEIDE Hälften noch mindestens minLenM lang sind
    if (sample.max - sample.min <= maxRise || len < minLenM * 2 - EPS) {
      out.push({ ...step, ...sample });
      return;
    }
    // Halbieren; die Ecken-Marker bleiben an den äußeren Hälften
    refine({ a: step.a, b: sample.mid, atStart: step.atStart, atEnd: false });
    refine({ a: sample.mid, b: step.b, atStart: false, atEnd: step.atEnd });
  };
  steps.forEach(refine);
  return out;
}

/**
 * Mittellinie + Breite aus einem band-förmigen Rechteck-Footprint (#684).
 *
 * Eine Mauer wird üblicherweise mit dem RECHTECK-Werkzeug gezogen
 * (`MAUER.allowedTools` beginnt mit RECTANGLE) — sie hat dann keine Polylinie,
 * aus der sich Abschnitte ableiten ließen, ist aber geometrisch trotzdem ein
 * Band. Aus einem langgestreckten Viereck lässt sich die Mittellinie eindeutig
 * gewinnen: Sie verbindet die Mittelpunkte der beiden KURZEN Kanten.
 *
 * Bewusst nur für Vierecke und erst ab einem Längenverhältnis von minAspect:
 * ein quadratisch gezeichneter Mauerklotz ist ein Volumenkörper und behält den
 * Ein-Anker-Ansatz wie ein Gebäude.
 *
 * @returns {{polyline: Array<{x,z}>, widthM: number}|null}
 */
export function centerlineFromFootprint(polygon, { minAspect = 3 } = {}) {
  if (!Array.isArray(polygon) || polygon.length !== 4) return null;

  const mid = (i, j) => ({
    x: (polygon[i].x + polygon[j].x) / 2,
    z: (polygon[i].z + polygon[j].z) / 2,
  });
  const edgeLen = (i) =>
    Math.hypot(polygon[(i + 1) % 4].x - polygon[i].x, polygon[(i + 1) % 4].z - polygon[i].z);

  // Kanten 0/2 und 1/3 liegen einander gegenüber
  const spanA = (edgeLen(0) + edgeLen(2)) / 2;
  const spanB = (edgeLen(1) + edgeLen(3)) / 2;
  const widthM = Math.min(spanA, spanB);
  const lengthM = Math.max(spanA, spanB);
  if (!(widthM > EPS) || lengthM / widthM < minAspect) return null;

  // Mittelpunkte der kurzen Kanten verbinden
  const polyline =
    spanA >= spanB
      ? [mid(1, 2), mid(3, 0)] // Kanten 1 und 3 sind kurz
      : [mid(0, 1), mid(2, 3)]; // Kanten 0 und 2 sind kurz
  return { polyline, widthM };
}

// ---------------------------------------------------------------------------
// Öffnungen (#698) — Bogenlängen-Rechnung auf der Polylinie
// ---------------------------------------------------------------------------

/** Gesamtlänge einer Polylinie in Metern. */
export function polylineLength(polyline) {
  if (!Array.isArray(polyline) || polyline.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    total += Math.hypot(polyline[i + 1].x - polyline[i].x, polyline[i + 1].z - polyline[i].z);
  }
  return total;
}

/**
 * Einen Weltpunkt auf die Polylinie projizieren (#698).
 *
 * Liefert die **Bogenlänge** des nächstgelegenen Punkts — die einzige Größe, in
 * der sich eine Öffnung robust ausdrücken lässt. Die 2D-Link-Parameter des Tors
 * (`t`, `edgeIndex`) taugen dafür nicht: Sie beziehen sich auf die 2D-Kanten des
 * Parents, während die 3D-Polylinie abgeleitet sein kann (Mittellinie bei
 * Rechteck-Mauern, #697). Seit #697 stimmen beide zwar überein, aber die
 * Kopplung wäre implizit und zerbräche beim nächsten Ableitungswechsel.
 *
 * @returns {{s: number, distance: number}|null}
 */
export function projectOntoPolyline(polyline, point) {
  if (!Array.isArray(polyline) || polyline.length < 2 || !point) return null;

  let travelled = 0;
  let best = null;

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const lenSq = dx * dx + dz * dz;
    if (!(lenSq > EPS)) continue;
    const len = Math.sqrt(lenSq);

    const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.z - a.z) * dz) / lenSq));
    const distance = Math.hypot(point.x - (a.x + dx * t), point.z - (a.z + dz * t));
    if (!best || distance < best.distance) best = { s: travelled + t * len, distance };

    travelled += len;
  }

  return best;
}

/**
 * Teilstück einer Polylinie zwischen zwei Bogenlängen (#698).
 *
 * Zwischenliegende Original-Stützpunkte bleiben erhalten, damit eine Öffnung
 * über eine gezeichnete Ecke hinweg nicht die Ecke selbst verschluckt.
 *
 * @returns {Array<{x,z}>|null} null bei entarteter Länge
 */
export function slicePolyline(polyline, fromM, toM) {
  if (!Array.isArray(polyline) || polyline.length < 2 || !(toM > fromM + EPS)) return null;

  const out = [];
  let travelled = 0;

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (!(len > EPS)) continue;

    const segStart = travelled;
    const segEnd = travelled + len;
    travelled = segEnd;
    if (segEnd <= fromM + EPS || segStart >= toM - EPS) continue;

    const at = (t) => ({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
    const t0 = Math.max(0, (fromM - segStart) / len);
    const t1 = Math.min(1, (toM - segStart) / len);
    if (out.length === 0) out.push(at(t0));
    out.push(at(t1));
  }

  return out.length >= 2 ? out : null;
}

/** Öffnungen klemmen, entartete verwerfen, überlappende zusammenfassen. */
const normalizeOpenings = (openings, totalM) => {
  if (!Array.isArray(openings) || openings.length === 0) return [];

  const clamped = openings
    .filter((o) => o && Number.isFinite(o.startM) && Number.isFinite(o.endM))
    .map((o) => ({
      startM: Math.max(0, Math.min(totalM, Math.min(o.startM, o.endM))),
      endM: Math.max(0, Math.min(totalM, Math.max(o.startM, o.endM))),
      headY: o.headY,
    }))
    .filter((o) => o.endM > o.startM + EPS)
    .sort((a, b) => a.startM - b.startM);

  const merged = [];
  for (const opening of clamped) {
    const prev = merged[merged.length - 1];
    if (prev && opening.startM <= prev.endM + EPS) {
      // Das größere Loch schließt das kleinere ein: der tiefere Sturz gewinnt,
      // und eine durchgehende Öffnung (headY null) schlägt jeden Sturz
      prev.endM = Math.max(prev.endM, opening.endM);
      prev.headY =
        prev.headY == null || opening.headY == null
          ? null
          : Math.min(prev.headY, opening.headY);
    } else {
      merged.push({ ...opening });
    }
  }
  return merged;
};

/**
 * Das Band in Stücke entlang der Polylinie zerlegen (#698).
 *
 * Kern der Öffnungs-Mechanik: Die Öffnung wird **oberhalb beider Renderpfade**
 * ausgedrückt, damit sie im flachen Garten (extrudeGeometry) genauso greift wie
 * am Hang (gestufte Abschnitte). `floorY = null` heißt „steht auf dem Gelände",
 * ein gesetztes `floorY` ist ein Sturz über einer Öffnung.
 *
 * Ohne Öffnungen ist das Ergebnis genau ein Stück über die volle Länge — der
 * Aufrufer erhält damit unverändert die bisherige Geometrie (Null-Regression).
 *
 * @param {Array<{startM, endM, headY}>} openings - headY = Weltoberkante der Öffnung
 * @returns {Array<{fromM, toM, floorY: number|null}>}
 */
export function planBandPieces(polyline, openings) {
  const totalM = polylineLength(polyline);
  if (!(totalM > EPS)) return [];

  const gaps = normalizeOpenings(openings, totalM);
  const pieces = [];
  let cursor = 0;

  for (const gap of gaps) {
    if (gap.startM > cursor + EPS) pieces.push({ fromM: cursor, toM: gap.startM, floorY: null });
    // headY null = durchgehende Öffnung bis zur Oberkante → gar kein Stück
    if (gap.headY != null) {
      pieces.push({ fromM: gap.startM, toM: gap.endM, floorY: gap.headY });
    }
    cursor = gap.endM;
  }
  if (cursor < totalM - EPS) pieces.push({ fromM: cursor, toM: totalM, floorY: null });

  return pieces;
}

/** Die vier Grundriss-Ecken eines Schritts (Umlauf mit +y-Winding). */
function stepCorners(step, halfW) {
  const dx = step.b.x - step.a.x;
  const dz = step.b.z - step.a.z;
  const len = Math.hypot(dx, dz) || 1;
  const d = { x: dx / len, z: dz / len };
  const n = { x: -d.z, z: d.x };

  // An inneren Ecken um die halbe Breite überstehen lassen: füllt die
  // Außenecke, ohne dass zwei gerade Nachbarschritte koplanar überlappen
  // (das gäbe Z-Fighting an jeder Schrittfuge).
  const a = step.atStart
    ? { x: step.a.x - d.x * halfW, z: step.a.z - d.z * halfW }
    : step.a;
  const b = step.atEnd ? { x: step.b.x + d.x * halfW, z: step.b.z + d.z * halfW } : step.b;

  return [
    { x: a.x + n.x * halfW, z: a.z + n.z * halfW },
    { x: b.x + n.x * halfW, z: b.z + n.z * halfW },
    { x: b.x - n.x * halfW, z: b.z - n.z * halfW },
    { x: a.x - n.x * halfW, z: a.z - n.z * halfW },
  ];
}

/**
 * Gestufte Band-Geometrie über dem Gelände bauen.
 *
 * @param {Array<{x,z}>} polyline - Quell-Polylinie in Welt-Metern (≥ 2 Punkte)
 * @param {object} params
 * @param {number} params.widthM - Bandbreite
 * @param {number} params.heightM - Bandhöhe über dem Gelände
 * @param {Function} params.elevationAt - Sampler aus createElevationSampler
 * @param {number} [params.stepM] - Schrittlänge (Default: stepLengthFor(heightM))
 * @param {Array<{startM, endM, headY}>} [params.openings] - Tor-Aussparungen (#698)
 * @returns {{ positions: Float32Array, indices: Uint32Array,
 *             steps: Array<{center: {x,z}, baseY, topY, isLintel}> }|null}
 */
export function buildBandGeometry(
  polyline,
  { widthM, heightM, elevationAt, stepM, maxRise, openings }
) {
  if (!Array.isArray(polyline) || polyline.length < 2) return null;
  if (!(widthM > 0) || !(heightM > 0) || typeof elevationAt !== 'function') return null;

  // Ohne Öffnungen ist das genau EIN Stück über die volle Länge — die Schleife
  // unten läuft dann exakt wie vor #698 (Null-Regression).
  const pieces = planBandPieces(polyline, openings);
  if (pieces.length === 0) return null;

  const halfW = widthM / 2;
  const positions = [];
  const indices = [];
  const meta = [];

  for (const piece of pieces) {
    const sub = slicePolyline(polyline, piece.fromM, piece.toM);
    if (!sub) continue;

    const raw = splitPolylineIntoSteps(sub, stepM ?? BAND_STEP_M);
    if (raw.length === 0) continue;
    const steps = refineStepsByRise(raw, elevationAt, maxRise ?? maxRiseFor(heightM));

    for (const step of steps) {
      const { mid, min, max } = step;
      const isLintel = piece.floorY != null;
      const baseY = isLintel ? piece.floorY : min;
      const topY = max + heightM;
      // Zu flacher Sturz ist ein Artefakt, kein Bauteil (§4 des Plans). Der
      // Test gilt NUR für Stürze — ein niedriges Band (Rasenkante, 5 cm) darf
      // davon nicht getroffen werden.
      if (isLintel && topY - baseY < MIN_LINTEL_M) continue;

      const corners = stepCorners(step, halfW);
      // 24 Vertices statt 8: geteilte Eckpunkte würden bei computeVertexNormals
      // über die Kanten gemittelt und den Quader rundlich schattieren.
      const quad = (v0, v1, v2, v3) => {
        const start = positions.length / 3;
        for (const v of [v0, v1, v2, v3]) positions.push(v.x, v.y, v.z);
        indices.push(start, start + 1, start + 2, start, start + 2, start + 3);
      };
      const at = (i, y) => ({ x: corners[i].x, y, z: corners[i].z });

      quad(at(0, topY), at(1, topY), at(2, topY), at(3, topY)); // Deckel (+y)
      quad(at(0, baseY), at(3, baseY), at(2, baseY), at(1, baseY)); // Boden (−y)
      for (let i = 0; i < 4; i++) {
        const j = (i + 1) % 4;
        quad(at(i, baseY), at(j, baseY), at(j, topY), at(i, topY)); // Flanken
      }

      meta.push({ center: mid, baseY, topY, isLintel });
    }
  }

  if (meta.length === 0) return null;

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    steps: meta,
  };
}

/**
 * baseY des Schritts, unter dem ein Punkt liegt (#611: verlinktes Tor).
 * Ohne das erbt das Paneel den Anker des ganzen Bands und hinge am gestuften
 * Zaun in der Luft oder in der Erde.
 */
export function stepBaseYAt(steps, point) {
  let best = null;
  let bestD = Infinity;
  for (const step of steps) {
    // Sturz-Abschnitte überspringen (#698): ihr baseY ist die Toroberkante —
    // der Torflügel hinge sonst ausgerechnet in seiner eigenen Öffnung.
    if (step.isLintel) continue;
    const d = Math.hypot(step.center.x - point.x, step.center.z - point.z);
    if (d < bestD) {
      bestD = d;
      best = step;
    }
  }
  return best ? best.baseY : null;
}

/**
 * Latten eines Zauns entlang der Polylinie verteilen (#700 zaun_typ).
 *
 * Sichtschutz bleibt das geschlossene Band (buildBandGeometry); Lattenzaun,
 * Staketen und Jägerzaun bestehen aus einzelnen senkrechten Latten mit Lücken.
 * Jede Latte ist ein kleiner Quader, den ExtrusionMesh setzt — anders als das
 * gestufte Band wird jede Latte einzeln auf ihre Geländehöhe gesetzt (elevationAt
 * je Lattenmitte), was das Hang-Stufen geschenkt mitnimmt.
 *
 * Eine verlinkte Tor-Öffnung wird als Bogenlängen-Intervall übergeben; Latten in
 * diesem Bereich entfallen, damit das Tor frei steht (dieselbe Mechanik wie die
 * Band-Aussparung, #698).
 *
 * @param {Array<{x,z}>} polyline - Quell-Polylinie in Welt-Metern (≥ 2 Punkte)
 * @param {object} params
 * @param {number} params.heightM - Lattenhöhe
 * @param {number} params.depthM - Lattentiefe (Bandbreite)
 * @param {number} params.slatWidthM - Lattenbreite entlang des Zauns
 * @param {number} params.gapM - Lücke zwischen zwei Latten
 * @param {Function} [params.elevationAt] - Sampler; ohne ihn liegt baseY bei 0
 * @param {Array<{startM, endM}>} [params.openings] - Tor-Aussparungen (Bogenlänge)
 * @returns {Array<{center:{x,z}, angleDeg, widthM, depthM, heightM, baseY}>|null}
 */
export function buildFenceSlats(
  polyline,
  { heightM, depthM, slatWidthM, gapM, elevationAt, openings = [] }
) {
  if (!Array.isArray(polyline) || polyline.length < 2) return null;
  if (!(heightM > 0) || !(depthM > 0) || !(slatWidthM > 0)) return null;
  const pitch = slatWidthM + Math.max(0, gapM);
  if (!(pitch > 0)) return null;

  const inOpening = (s) => openings.some((o) => s >= o.startM && s <= o.endM);
  const slats = [];
  let acc = 0; // Bogenlänge bis zum Segmentbeginn

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const segLen = Math.hypot(dx, dz);
    if (segLen < EPS) continue;
    const angleDeg = (Math.atan2(dz, dx) * 180) / Math.PI;

    // Erste Latte eine halbe Teilung nach Segmentbeginn, dann im Pitch-Raster.
    for (let d = pitch / 2; d < segLen; d += pitch) {
      const s = acc + d;
      if (inOpening(s)) continue;
      const t = d / segLen;
      const center = { x: a.x + dx * t, z: a.z + dz * t };
      slats.push({
        center,
        angleDeg,
        widthM: slatWidthM,
        depthM,
        heightM,
        baseY: typeof elevationAt === 'function' ? elevationAt(center) : 0,
      });
    }
    acc += segLen;
  }

  return slats.length ? slats : null;
}
